import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { motion } from "motion/react";
import { ApiClientError, type PassageRonde } from "@victor/api-client";
import { api } from "../../lib/api";
import { tryGetCurrentPosition } from "../../lib/geolocation";
import { useAuth } from "../../auth/AuthContext";
import { Logo } from "../../ui/Logo";
import { enqueueOrSend } from "../../lib/sync-manager";

/**
 * Vague 1 · Phase C.3.b · Page d'atterrissage d'un QR de point de ronde.
 *
 * Le QR contient `https://agent.myvictor.fr/scan/<pointRondeId>`. Quand
 * l'agent scanne avec son scanner caméra système (iPhone Camera /
 * Google Lens / etc.), la PWA s'ouvre sur cette route.
 *
 * Flow :
 *  1. Si pas auth → AuthGuard redirige vers /login (avec returnTo)
 *  2. Capture la geoloc en best-effort
 *  3. POST /v1/me/rondes/scan → API choisit la vacation courante
 *  4. Affiche succès (PR libellé + écart) ou erreur typée
 *  5. CTA retour à la journée
 */
export function ScanPage() {
  const { pointRondeId } = useParams<{ pointRondeId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [state, setState] = useState<
    | { kind: "scanning" }
    | { kind: "ok"; passage: PassageRonde }
    | { kind: "queued" }
    | { kind: "ko"; code: string; message: string }
  >({ kind: "scanning" });

  useEffect(() => {
    if (!pointRondeId) {
      setState({
        kind: "ko",
        code: "invalid_qr",
        message: "QR code invalide ou illisible.",
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { position } = await tryGetCurrentPosition({ timeoutMs: 5000 });
        if (cancelled) return;
        const payload = {
          pointRondeId,
          latitude: position?.latitude ?? null,
          longitude: position?.longitude ?? null,
          precisionMetres: position?.precisionMetres ?? null,
          scanneeClientAt: new Date().toISOString(),
          methode: "qr_code" as const,
        };
        const res = await enqueueOrSend(
          "ronde_scan",
          payload,
          undefined,
          () => api.me.scanRonde(payload),
        );
        if (cancelled) return;
        if (res.kind === "sent") {
          setState({ kind: "ok", passage: res.result });
        } else {
          setState({ kind: "queued" });
        }
      } catch (err) {
        if (cancelled) return;
        setState({ kind: "ko", ...interpretError(err) });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pointRondeId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <header className="px-5 py-4">
        <Link to="/" className="inline-flex items-center gap-2">
          <Logo size={28} />
          <span className="font-display text-lg font-black tracking-tight text-navy-800">
            victor
          </span>
        </Link>
      </header>

      <main className="mx-auto max-w-md px-5 py-8">
        {state.kind === "scanning" ? <Scanning /> : null}
        {state.kind === "ok" ? <Success passage={state.passage} /> : null}
        {state.kind === "queued" ? <Queued /> : null}
        {state.kind === "ko" ? (
          <Failure code={state.code} message={state.message} />
        ) : null}

        <div className="mt-8 space-y-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="block w-full rounded-xl bg-accent-500 px-4 py-3 text-center text-sm font-bold text-white shadow-sm transition-colors hover:bg-accent-600"
          >
            Retour à ma journée
          </button>
          {user ? null : (
            <p className="text-center text-xs text-navy-500">
              Connectez-vous pour enregistrer ce scan.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Scanning() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-navy-100"
    >
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-accent-50 text-accent-500">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          aria-hidden="true"
          className="animate-spin"
        >
          <circle cx="12" cy="12" r="9" opacity="0.25" />
          <path d="M21 12a9 9 0 0 0-9-9" />
        </svg>
      </div>
      <p className="font-display text-lg font-black text-navy-800">
        Enregistrement du passage…
      </p>
      <p className="mt-1 text-sm text-navy-500">
        Capture position et envoi sécurisé.
      </p>
    </motion.div>
  );
}

function Queued() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-2xl bg-amber-50 p-6 text-center ring-1 ring-amber-200"
    >
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <polyline points="12 7 12 12 15 14" />
        </svg>
      </div>
      <p className="font-display text-xl font-black text-amber-900">
        Hors réseau
      </p>
      <p className="mt-2 text-sm text-amber-800">
        Scan enregistré localement. Synchronisation automatique au retour du
        signal.
      </p>
    </motion.div>
  );
}

function Success({ passage }: { passage: PassageRonde }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-navy-100"
    >
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <p className="font-display text-xl font-black text-navy-800">
        Passage enregistré
      </p>
      <p className="mt-1 text-sm text-navy-500">
        {formatTime(passage.scanneeAt)}
      </p>
      {passage.ecartMetres != null ? (
        <p className="mt-3 inline-block rounded-full bg-navy-50 px-3 py-1 text-xs font-semibold text-navy-600">
          À {passage.ecartMetres} m du point
        </p>
      ) : null}
    </motion.div>
  );
}

function Failure({ code, message }: { code: string; message: string }) {
  const isBlocking = code !== "invalid_qr";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`rounded-2xl p-6 text-center ring-1 ${
        isBlocking
          ? "bg-red-50 ring-red-200 text-red-900"
          : "bg-amber-50 ring-amber-200 text-amber-900"
      }`}
    >
      <div
        className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full ${
          isBlocking ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
        }`}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="8" x2="12" y2="13" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      </div>
      <p className="font-display text-xl font-black">Scan refusé</p>
      <p className="mt-2 text-sm">{message}</p>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------

function interpretError(err: unknown): { code: string; message: string } {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case "no_active_vacation":
        return {
          code: err.code,
          message:
            "Pas de vacation en cours. Pointez votre prise de poste avant de scanner.",
        };
      case "not_your_vacation":
        return {
          code: err.code,
          message: "Cette vacation ne vous est pas affectée.",
        };
      case "point_ronde_wrong_site":
        return {
          code: err.code,
          message:
            "Ce point de ronde n'appartient pas au site de votre vacation.",
        };
      case "point_ronde_archived":
        return {
          code: err.code,
          message: "Ce point de ronde a été retiré du référentiel.",
        };
      case "point_ronde_not_found":
        return {
          code: err.code,
          message: "Ce point de ronde n'existe pas (ou plus).",
        };
      case "vacation_annulee":
        return {
          code: err.code,
          message: "La vacation a été annulée.",
        };
      case "agent_not_found":
        return {
          code: err.code,
          message: "Votre compte n'est pas relié à un agent terrain.",
        };
      case "forbidden":
        return {
          code: err.code,
          message: "Vous n'avez pas la permission de scanner.",
        };
      default:
        return { code: err.code, message: err.message || "Échec du scan." };
    }
  }
  return {
    code: "unknown",
    message: "Échec du scan. Vérifiez votre connexion.",
  };
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
